import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FileQuestion, PlusCircle, Trash2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { examQuestionsApi, examsApi } from "../services/api.js";

const emptyOption = { text: "", isCorrect: false };

export default function ExamQuestionsPage() {
  const { examId } = useParams();

  const [exam, setExam] = useState(null);
  const [examInfo, setExamInfo] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [subjectsLoading, setSubjectsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState({
    title: "",
    subject: "",
    imageUrl: "",
    explanation: "",
    options: [
      { ...emptyOption },
      { ...emptyOption },
      { ...emptyOption },
      { ...emptyOption },
    ],
  });

  useEffect(() => {
    loadPageData();
  }, [examId]);

  useEffect(() => {
    const loadSubjects = async () => {
      if (!exam?.group) {
        setSubjects([]);
        setFormData((prev) => ({ ...prev, subject: "" }));
        return;
      }

      try {
        setSubjectsLoading(true);

        const data = await examsApi.getSubjectsByGroup(exam.group);

        const rawSubjects = Array.isArray(data?.data) ? data.data : [];

        const normalizedSubjects = rawSubjects
          .map((item) => {
            if (typeof item === "string") return item;
            if (item?.name) return item.name;
            if (item?.title) return item.title;
            if (item?.label) return item.label;
            return "";
          })
          .filter(Boolean);

        setSubjects(normalizedSubjects);

        setFormData((prev) => ({
          ...prev,
          subject: normalizedSubjects.includes(prev.subject) ? prev.subject : "",
        }));
      } catch (error) {
        console.error("Subjects loading error:", error);
        setSubjects([]);
        setFormData((prev) => ({ ...prev, subject: "" }));
      } finally {
        setSubjectsLoading(false);
      }
    };

    loadSubjects();
  }, [exam?.group]);

  const loadPageData = async () => {
    try {
      setLoading(true);

      const [examRes, questionsRes] = await Promise.all([
        examsApi.getById(examId),
        examQuestionsApi.getByExam(examId),
      ]);

      setExam(examRes.data || null);
      setExamInfo(questionsRes.examInfo || null);
      setQuestions(questionsRes.data || []);
    } catch (error) {
      console.error("Exam questions loading error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleMainChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubjectChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      subject: value,
    }));
  };

  const handleOptionChange = (index, field, value) => {
    const updatedOptions = [...formData.options];
    updatedOptions[index][field] = value;

    setFormData((prev) => ({
      ...prev,
      options: updatedOptions,
    }));
  };

  const addOption = () => {
    setFormData((prev) => ({
      ...prev,
      options: [...prev.options, { ...emptyOption }],
    }));
  };

  const removeOption = (index) => {
    if (formData.options.length <= 2) return;

    const updatedOptions = formData.options.filter((_, i) => i !== index);

    setFormData((prev) => ({
      ...prev,
      options: updatedOptions,
    }));
  };

  // const handleCreateQuestion = async (e) => {
  //   e.preventDefault();

  //   const validOptions = formData.options.filter((opt) => opt.text.trim() !== "");

  //   if (!formData.title.trim()) {
  //     alert("Sual başlığı boş ola bilməz");
  //     return;
  //   }

  //   if (!formData.subject) {
  //     alert("Fənn seçilməlidir");
  //     return;
  //   }

  //   if (validOptions.length < 2) {
  //     alert("Ən azı 2 variant yazılmalıdır");
  //     return;
  //   }

  //   const correctOptionsCount = validOptions.filter((opt) => opt.isCorrect).length;

  //   if (correctOptionsCount < 1) {
  //     alert("Ən azı 1 doğru cavab seçilməlidir");
  //     return;
  //   }

  //   try {
  //     setSubmitting(true);

  //     await examQuestionsApi.create({
  //       exam: examId,
  //       subject: formData.subject,
  //       title: formData.title,
  //       imageUrl: formData.imageUrl,
  //       explanation: formData.explanation,
  //       options: validOptions,
  //     });

  //     setFormData({
  //       title: "",
  //       subject: "",
  //       imageUrl: "",
  //       explanation: "",
  //       options: [
  //         { ...emptyOption },
  //         { ...emptyOption },
  //         { ...emptyOption },
  //         { ...emptyOption },
  //       ],
  //     });

  //     await loadPageData();
  //   } catch (error) {
  //     console.error("Create question error:", error);
  //     alert(error?.response?.data?.message || "Sual yaradılarkən xəta baş verdi");
  //   } finally {
  //     setSubmitting(false);
  //   }
  // };


  const handleSubmitQuestion = async (e) => {
  e.preventDefault();

  const validOptions = formData.options.filter(
    (opt) => opt.text.trim() !== ""
  );

  if (!formData.title.trim()) return alert("Sual başlığı boş ola bilməz");
  if (!formData.subject) return alert("Fənn seçilməlidir");
  if (validOptions.length < 2) return alert("Ən azı 2 variant yazılmalıdır");

  const correctOptionsCount = validOptions.filter((opt) => opt.isCorrect).length;
  if (correctOptionsCount < 1) return alert("Ən azı 1 doğru cavab seçilməlidir");

  try {
    setSubmitting(true);

    const payload = {
      exam: examId,
      subject: formData.subject,
      title: formData.title,
      imageUrl: formData.imageUrl,
      explanation: formData.explanation,
      options: validOptions,
    };

    if (editingId) {
      // UPDATE
      await examQuestionsApi.update(editingId, payload);
    } else {
      // CREATE
      await examQuestionsApi.create(payload);
    }

    setFormData({
      title: "",
      subject: "",
      imageUrl: "",
      explanation: "",
      options: [{ ...emptyOption }, { ...emptyOption }],
    });

    setEditingId(null);

    await loadPageData();
  } catch (error) {
    console.error(error);
    alert(error?.response?.data?.message || "Xəta baş verdi");
  } finally {
    setSubmitting(false);
  }
};

  const handleEditClick = (question) => {
  setEditingId(question._id);

  setFormData({
    title: question.title || "",
    subject: question.subject || "",
    imageUrl: question.imageUrl || "",
    explanation: question.explanation || "",
    options: question.options?.length
      ? question.options
      : [{ ...emptyOption }, { ...emptyOption }],
  });

  window.scrollTo({ top: 0, behavior: "smooth" });
};

  const handleDeleteQuestion = async (id) => {
    const confirmDelete = window.confirm("Bu sual silinsin?");
    if (!confirmDelete) return;

    try {
      await examQuestionsApi.delete(id);
      await loadPageData();
    } catch (error) {
      console.error("Delete question error:", error);
    }
  };


 const groupedQuestions = questions.reduce((acc, question) => {
  const subject = question.subject || "Digər";

  if (!acc[subject]) acc[subject] = [];
  acc[subject].push(question);

  return acc;
}, {});
  

  return (
    <Layout
      breadcrumbs={[
        { label: "Examlar", href: "/exams" },
        { label: exam?.name || "Suallar" },
      ]}
    >
      <div className="space-y-8" data-testid="exam-questions-page">
        <div>
          <h1
            className="text-2xl font-bold text-slate-900"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            {loading ? "Yükleniyor..." : `${exam?.name} - Suallar`}
          </h1>

          <p className="text-slate-500 mt-1">
            Bu exam üçün sual əlavə et və idarə et
          </p>

          {examInfo && (
            <div className="mt-3 rounded-xl border border-teal-100 bg-teal-50 px-4 py-3 text-sm text-slate-700">
              <p>
                <span className="font-semibold">Qrup:</span> {examInfo.group || exam?.group} {exam._id}
              </p>
              <p>
                <span className="font-semibold">Hər sualın balı:</span>{" "}
                {examInfo.questionPoint}
              </p>
              <p className="mt-1 text-slate-600">{examInfo.rule}</p>

              <div className="flex flex-wrap gap-2 mt-4">
  {Object.entries(groupedQuestions).map(([subject, items]) => (
    <div
      key={subject}
      className="px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-sm border border-slate-200"
    >
      {subject}: <span className="font-semibold">{items.length}</span>
    </div>
  ))}
</div>
            </div>
          )}
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <PlusCircle className="w-5 h-5 text-teal-600" />
              Yeni Sual Ekle
            </CardTitle>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmitQuestion} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  type="text"
                  name="title"
                  placeholder="Sual başlığı"
                  value={formData.title}
                  onChange={handleMainChange}
                  className="h-11 rounded-lg border border-slate-200 px-3 outline-none focus:border-teal-500"
                  required
                />

                <Select
                  value={formData.subject}
                  onValueChange={handleSubjectChange}
                  disabled={subjectsLoading || subjects.length === 0}
                >
                  <SelectTrigger className="h-11 rounded-lg border border-slate-200 px-3">
                    <SelectValue
                      placeholder={
                        subjectsLoading
                          ? "Yüklənir..."
                          : subjects.length === 0
                          ? "Fənn tapılmadı"
                          : "Fənn seç"
                      }
                    />
                  </SelectTrigger>

                  <SelectContent>
                    {subjects.map((subject) => (
                      <SelectItem key={subject} value={subject}>
                        {subject}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                <input
                  type="text"
                  name="imageUrl"
                  placeholder="Resim URL"
                  value={formData.imageUrl}
                  onChange={handleMainChange}
                  className="h-11 rounded-lg border border-slate-200 px-3 outline-none focus:border-teal-500"
                />
              </div>

              <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-700">
                Bu exam üçün bal backend tərəfindən avtomatik təyin olunur:
                <span className="ml-1 font-semibold">
                  {examInfo?.questionPoint ?? "-"} bal
                </span>
              </div>

              <textarea
                name="explanation"
                placeholder="Açıklama"
                value={formData.explanation}
                onChange={handleMainChange}
                rows={4}
                className="w-full rounded-lg border border-slate-200 px-3 py-3 outline-none focus:border-teal-500"
              />

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-slate-900">Cavab Variantları</h3>
                  <button
                    type="button"
                    onClick={addOption}
                    className="text-sm text-teal-600 hover:text-teal-700"
                  >
                    + Variant ekle
                  </button>
                </div>

                {formData.options.map((option, index) => (
                  <div
                    key={index}
                    className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-3 items-center"
                  >
                    <input
                      type="text"
                      placeholder={`Variant ${index + 1}`}
                      value={option.text}
                      onChange={(e) =>
                        handleOptionChange(index, "text", e.target.value)
                      }
                      className="h-11 rounded-lg border border-slate-200 px-3 outline-none focus:border-teal-500"
                    />

                    <label className="flex items-center gap-2 text-sm text-slate-600">
                      <input
                        type="checkbox"
                        checked={option.isCorrect}
                        onChange={(e) =>
                          handleOptionChange(index, "isCorrect", e.target.checked)
                        }
                      />
                      Doğru
                    </label>

                    <button
                      type="button"
                      onClick={() => removeOption(index)}
                      className="p-2 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="h-11 px-5 rounded-lg bg-teal-600 text-white font-medium hover:bg-teal-700 transition-colors disabled:opacity-50"
              >
                {submitting ? "Kaydediliyor..." : "Sualı Kaydet"}
              </button>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Bu Examın Sualları</CardTitle>
          </CardHeader>

          <CardContent>
            {loading ? (
              <p className="text-slate-500">Yükleniyor...</p>
            ) : questions.length === 0 ? (
              <div className="text-center py-10">
                <FileQuestion className="w-12 h-12 text-slate-300 mx-auto" />
                <p className="text-slate-500 mt-3">Henüz sual əlavə olunmayıb</p>
              </div>
            ) : (
              // <div className="space-y-4">
              //   {questions.map((question, index) => (
              //     <Card key={question._id} className="border border-slate-200">
              //       <CardContent className="pt-6">
              //         <div className="flex items-start justify-between gap-4">
              //           <div className="flex-1 space-y-3">
              //             <div>
              //               <p className="text-xs text-slate-400">Sual {index + 1}</p>
              //               <h3 className="font-semibold text-slate-900">
              //                 {question.title}
              //               </h3>
              //               {question.subject && (
              //                 <p className="text-sm text-slate-500 mt-1">
              //                   Fənn: {question.subject}
              //                 </p>
              //               )}
              //             </div>

              //             {question.imageUrl && (
              //               <img
              //                 src={question.imageUrl}
              //                 alt={question.title}
              //                 className="w-full max-w-md rounded-lg border"
              //               />
              //             )}

              //             <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              //               {question.options?.map((opt, i) => (
              //                 <div
              //                   key={i}
              //                   className={`rounded-lg border px-3 py-2 text-sm ${
              //                     opt.isCorrect
              //                       ? "border-green-200 bg-green-50 text-green-700"
              //                       : "border-slate-200 bg-slate-50 text-slate-700"
              //                   }`}
              //                 >
              //                   {opt.text}
              //                 </div>
              //               ))}
              //             </div>

              //             {question.explanation && (
              //               <div className="rounded-lg bg-blue-50 px-3 py-2 text-sm text-slate-700">
              //                 <span className="font-medium">Açıklama:</span>{" "}
              //                 {question.explanation}
              //               </div>
              //             )}

              //             <div className="flex gap-4 text-sm text-slate-500">
              //               <span>Bal: {question.bal || examInfo?.questionPoint || "-"}</span>
              //             </div>
              //           </div>

              //           <button
              //             onClick={() => handleDeleteQuestion(question._id)}
              //             className="p-2 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors"
              //           >
              //             <Trash2 className="w-4 h-4" />
              //           </button>
              //         </div>
              //       </CardContent>
              //     </Card>
              //   ))}
              // </div>
              <div className="space-y-8">
  {Object.entries(groupedQuestions).map(([subject, subjectQuestions]) => (
    <div key={subject} className="space-y-4">
      
      {/* SUBJECT HEADER */}
     
      {/* QUESTIONS */}
      <div className="space-y-4">
        {subjectQuestions.map((question, index) => (
          <Card key={question._id} className="border border-slate-200">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 space-y-3">

                  <div className="flex flex-col  gap-3">
                    <p className="text-xs text-slate-400">
                      Sual {index + 1}
                    </p>

                    <h3 className="font-semibold text-slate-900">
                      {question.title}
                    </h3>


                     <h6 className="font-semibold text-slate-500 ">
                      Fənn: {question.subject}
                    </h6>
                  </div>

                  {question.imageUrl && (
                    <img
                      src={question.imageUrl}
                      alt={question.title}
                      className="w-full max-w-md rounded-lg border"
                    />
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {question.options?.map((opt, i) => (
                      <div
                        key={i}
                        className={`rounded-lg border px-3 py-2 text-sm ${
                          opt.isCorrect
                            ? "border-green-200 bg-green-50 text-green-700"
                            : "border-slate-200 bg-slate-50 text-slate-700"
                        }`}
                      >
                        {opt.text}
                      </div>
                    ))}
                  </div>

                  {question.explanation && (
                    <div className="rounded-lg bg-blue-50 px-3 py-2 text-sm text-slate-700">
                      <span className="font-medium">Açıklama:</span>{" "}
                      {question.explanation}
                    </div>
                  )}

                  <div className="flex gap-4 text-sm text-slate-500">
                    <span>
                      Bal: {question.bal || examInfo?.questionPoint || "-"}
                    </span>
                  </div>
                </div>
                <button
  onClick={() => handleEditClick(question)}
  className="p-2 rounded-lg hover:bg-blue-50 text-slate-400 hover:text-blue-600 transition-colors"
>
  Edit
</button>

                <button
                  onClick={() => handleDeleteQuestion(question._id)}
                  className="p-2 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  ))}
</div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}